import { useState, useEffect, ComponentType } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  HelpCircle,
  Calendar,
  MapPin,
  BarChart,
  User,
  ChevronDown,
  ChevronUp,
  Send,
  Loader2,
  CheckCircle,
  Book,
  Search,
  Settings,
  Sparkles,
  Star,
  Award,
  Ship,
  Smartphone,
  ExternalLink,
  Globe,
  Users,
  Link2,
  LogIn,
  UserPlus
} from 'lucide-react';
import toast from 'react-hot-toast';
import { doc, collection, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Capacitor } from '@capacitor/core';

interface FeedbackFormData {
  subject: string;
  message: string;
  type: 'feedback' | 'support' | 'bug' | 'feature';
}

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: 'getting-started' | 'cruises' | 'account' | 'features' | 'friends';
}

interface FeatureItem {
  id: number;
  title: string;
  description: string;
  status: 'new';
  icon: ComponentType<{ className?: string }>;
  version?: string;
}

const Help = () => {
  const { currentUser, preferredDisplayName } = useAuth();
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset } = useForm<FeedbackFormData>();

  const featuresData: FeatureItem[] = [
    {
      id: 27,
      title: "Share with Friends",
      description: "Connect with friends and share your cruise adventures! Add friends by email and choose between Full access (automatic calendar visibility) or Restricted access (share specific cruises). View friends' cruises, detect when you're traveling together, and use the calendar overlay to compare schedules and find overlapping availability for planning cruises together.",
      status: 'new',
      icon: Users,
      version: "v3.0.0"
    },
    {
      id: 19,
      title: "Expanded Loyalty Program Integration",
      description: "Added support for tracking loyalty status and progression across Disney (Castaway Club), Holland America (Mariner Society), Norwegian (Latitudes Rewards), and Princess (Captain's Circle).",
      status: 'new',
      icon: Award,
      version: "v3.1.0"
    },
    {
      id: 29,
      title: "Royal Caribbean Points Choice",
      description: "You can now customize which loyalty program your points accrue to for participating sister-brands! After completing a Royal Caribbean or Celebrity cruise, the Post-Cruise page will let you designate the destination program and automatically handle the cross-brand calculations.",
      status: 'new',
      icon: Sparkles,
      version: "v3.1.0"
    },
    {
      id: 30,
      title: "Official Itinerary Sync",
      description: "Keep your port schedules perfectly up-to-date with official cruise itineraries! Synced cruises receive automatic port updates from official sources. You can view sync badges directly on your cruise pages, break the synchronization link to make manual edits at any time, or re-link whenever you want. You'll also receive notifications when an official itinerary becomes available for one of your unlinked cruises.",
      status: 'new',
      icon: Link2,
      version: "v3.2.0"
    },
    {
      id: 31,
      title: "Google & Apple Sign-In",
      description: "You can now sign in or create your account effortlessly using your Google or Apple credentials! Enjoy fast, secure single sign-on (SSO) across all your devices without remembering separate passwords.",
      status: 'new',
      icon: LogIn,
      version: "v3.3.0"
    },
    {
      id: 32,
      title: "Integrated Dashboard Friends Data",
      description: "Stay connected with your cruise companions directly from your main dashboard! Easily see friends who are currently sailing at sea, view upcoming shared cruises, and browse past joint voyages with friends.",
      status: 'new',
      icon: Users,
      version: "v3.3.0"
    },
    {
      id: 33,
      title: "Share Cruise & Itinerary with Friends",
      description: "Effortlessly invite travel companions to join your cruise directly from your cruise details page. Inviting a friend sends them a notification to accept and import the complete sailing itinerary straight into their upcoming cruises!",
      status: 'new',
      icon: UserPlus,
      version: "v3.3.0"
    },
    {
      id: 34,
      title: "Carnival Rewards Loyalty Program",
      description: "Full support for Carnival's new spend-based Rewards program! Features automatic conversion from the legacy VIFP Club with grandfathered tier protection based on past sailing days, 2-year cycle tracking, and post-cruise Star entry.",
      status: 'new',
      icon: Award,
      version: "v3.4.0"
    },
  ];

  const faqItems: FAQItem[] = [
    {
      id: 1,
      question: "How do I add my first cruise?",
      answer: "Click the 'Add Cruise' button in the navigation bar or on your dashboard. Fill in the required information including cruise line, ship name, departure date, and cabin details. You can also add optional information like ports of call and travel arrangements.",
      category: 'getting-started'
    },
    {
      id: 2,
      question: "Can I track multiple cruises at once?",
      answer: "Yes! You can add as many cruises as you want. Your dashboard will show upcoming cruises prominently, while past cruises are organized in a separate section for easy reference.",
      category: 'cruises'
    },
    {
      id: 3,
      question: "How do I add ports of call to my cruise?",
      answer: "When adding or editing a cruise, scroll down to the 'Ports of Call' section and click 'Add Port'. You can include the port name, date, arrival/departure times, and planned excursions.",
      category: 'cruises'
    },
    {
      id: 4,
      question: "What is a guaranteed cabin?",
      answer: "A guaranteed cabin means you've booked a specific cabin category (like balcony or suite) but the cruise line will assign your exact cabin number closer to departure. Check this option when adding your cruise if you have a guaranteed booking.",
      category: 'cruises'
    },
    {
      id: 5,
      question: "How do I track my travel arrangements?",
      answer: "In the cruise form, you can add flights, trains, and rental cars in the 'Travel Details' section. Include confirmation numbers, times, and locations to keep everything organized in one place.",
      category: 'features'
    },
    {
      id: 6,
      question: "Can I see statistics about my cruising history?",
      answer: "Yes! Visit the 'Highlights' page to see your cruise statistics including total nights at sea, ports visited, cruise lines sailed with, and upcoming cruise countdowns.",
      category: 'features'
    },
    {
      id: 7,
      question: "How do I change my account information?",
      answer: "Click on your name in the navigation bar and select 'Profile' to update your display name, email address, or password. You can also set a preferred display name that's different from your full name.",
      category: 'account'
    },
    {
      id: 8,
      question: "What are casino comps and how do I track them?",
      answer: "Casino comps are complimentary cruises earned through casino play. When adding a cruise, check the 'complimentary casino sailing' option. Your highlights page will show the total value of your casino comps.",
      category: 'cruises'
    },
    {
      id: 9,
      question: "Can I edit or delete a cruise after adding it?",
      answer: "Yes! Click on any cruise to view its details, then use the 'Edit' or 'Delete' buttons. You can modify any information or remove the cruise entirely if needed.",
      category: 'cruises'
    },
    {
      id: 10,
      question: "How does the cruise calendar work?",
      answer: "The calendar on your dashboard highlights dates when you have cruises. Click on any highlighted date to see which cruises are active on that day. This helps you visualize your cruise schedule at a glance.",
      category: 'features'
    },
    {
      id: 11,
      question: "What happens if my new cruise dates conflict with an existing reservation?",
      answer: "The system will automatically detect date conflicts and show you a 'Date Conflict Detected' modal when you try to save. This prevents accidental double-bookings, but allows for back-to-back cruises where one cruise ends and another begins on the same day. You can choose to 'Cancel & Review Dates' to fix the conflict, or 'Continue Anyway' if the overlap is intentional.",
      category: 'features'
    },
    {
      id: 12,
      question: "How do I mark a cruise as completed and add post-trip details?",
      answer: "When adding or editing a cruise with a past departure date, the system will prompt you to confirm if the cruise is completed. You can also navigate to the 'Post Cruise Details' page from your dashboard for recently returned cruises. Here you can add onboard expenses, gambling wins/losses, and additional notes about your experience. Saving these details will automatically mark the cruise as completed and update your cruise statistics.",
      category: 'cruises'
    },
    {
      id: 13,
      question: "What information can I track for completed cruises?",
      answer: "For completed cruises, you can track onboard expenses (spa, dining, shopping, etc.), gambling results (wins and losses), and personal notes about your experience. This helps you keep a complete record of your cruise costs and memories for future reference.",
      category: 'cruises'
    },
    {
      id: 14,
      question: "How do I add a friend?",
      answer: "Go to the Friends page and click the 'Add Friend' button. Enter your friend's email address and choose an access level: Full access (they see your entire cruise calendar automatically) or Restricted access (you control which cruises they can see). Your friend will receive a notification and can accept or decline the request.",
      category: 'friends'
    },
    {
      id: 15,
      question: "What's the difference between Full access and Restricted access friends?",
      answer: "Full access friends can see your entire cruise calendar automatically - all your upcoming and past cruises without you needing to share each one individually. Restricted access friends can only see cruises you explicitly share with them. You control which cruises they can view on a per-cruise basis. You can change a friend's access level at any time from the Friends page.",
      category: 'friends'
    },
    {
      id: 16,
      question: "How do I share a cruise with a Restricted access friend?",
      answer: "On any cruise details page, click the 'Share with Friends' button. This will show a list of your Restricted access friends (Full access friends automatically see all cruises). Toggle the switch next to each friend's name to share or unshare that specific cruise with them. You can share or unshare cruises at any time.",
      category: 'friends'
    },
    {
      id: 17,
      question: "How do I view a friend's cruises?",
      answer: "Go to the Friends page and click on any friend's name or the 'View Cruises' button next to them. This will show you their cruise calendar based on their access level to you. If you have Full access, you'll see all their cruises. If you have Restricted access, you'll only see cruises they've explicitly shared with you.",
      category: 'friends'
    },
    {
      id: 28,
      question: "What information can my friends see about my cruises?",
      answer: "Friends can see basic cruise information only: cruise line, ship name, departure and return dates, departure port, and ports of call (port names and times only—no excursion details). They cannot see cabin numbers, booking numbers, costs, gambling details, travel agent information, personal notes, dining time, drink or internet packages, travel details (flights, rental cars, etc.), or ratings.",
      category: 'friends'
    },
    {
      id: 29,
      question: "How do I change a friend's access level?",
      answer: "Go to the Friends page, find the friend you want to modify, and click the settings icon next to their name. Select 'Change Access Level' and choose between Full access or Restricted access. If you downgrade from Full access to Restricted access, they will lose access to cruises you haven't explicitly shared.",
      category: 'friends'
    },
    {
      id: 30,
      question: "What is the calendar overlay feature?",
      answer: "When viewing a friend's cruises, you can use the calendar overlay to compare your calendar with theirs. Toggle switches let you show or hide your calendar and your friend's calendar independently. The overlay highlights dates where you're both available (great for planning cruises together), dates where you both have cruises, and dates where only one of you is busy.",
      category: 'friends'
    },
    {
      id: 31,
      question: "How do I remove a friend?",
      answer: "Go to the Friends page, find the friend you want to remove, and click the remove/trash icon next to their name. Confirm the removal in the dialog that appears. Once removed, they will no longer be able to see your cruises, and you won't be able to see theirs. You can send them a new friend request later if you want to reconnect.",
      category: 'friends'
    }
  ];

  const categories = [
    { value: 'all', label: 'All Questions' },
    { value: 'getting-started', label: 'Getting Started' },
    { value: 'cruises', label: 'Managing Cruises' },
    { value: 'features', label: 'Features' },
    { value: 'friends', label: 'Friends & Sharing' },
    { value: 'account', label: 'Account Settings' }
  ];

  const filteredFAQs = selectedCategory === 'all'
    ? faqItems
    : faqItems.filter(item => item.category === selectedCategory);

  const toggleFAQ = (id: number) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  const onSubmitFeedback = async (data: FeedbackFormData) => {
    if (!currentUser) {
      toast.error('You must be logged in to send feedback');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get the display name (preferred or full name)
      const displayName = preferredDisplayName || currentUser.displayName || 'User';

      // Create document reference
      const ref = doc(collection(db, 'supportMessages'));

      // Determine platform
      const platform = Capacitor.isNativePlatform()
        ? Capacitor.getPlatform()
        : 'web';

      const messageData = {
        userId: currentUser.uid,
        userName: displayName,
        userEmail: currentUser.email || '',
        type: data.type,
        subject: data.subject,
        message: data.message,
        status: 'pending' as const,
        createdAt: serverTimestamp(),
        platform: platform,
      };

      // Check if online and try immediate send
      if (navigator.onLine) {
        try {
          // Try to send immediately
          await setDoc(ref, messageData);
          setIsSubmitted(true);
          toast.success('Thank you for your feedback! We\'ll get back to you soon.');
          reset();
          setTimeout(() => setIsSubmitted(false), 5000);
          setIsSubmitting(false);
          return; // Success, exit early
        } catch (error) {
          // If immediate send fails, fall through to offline queue
          console.warn('Immediate send failed, queueing offline:', error);
        }
      }

      // Offline or immediate send failed - queue locally
      setDoc(ref, messageData)
        .then(() => {
          console.debug('[support] queued offline; will sync when online', { id: ref.id });

          // Store message ID in localStorage as backup
          try {
            const pendingMessages = JSON.parse(
              localStorage.getItem('pendingSupportMessages') || '[]'
            );
            pendingMessages.push({
              id: ref.id,
              timestamp: Date.now(),
              subject: data.subject
            });
            localStorage.setItem('pendingSupportMessages', JSON.stringify(pendingMessages));
          } catch {
            // Ignore localStorage errors
          }
        })
        .catch((error) => {
          console.error('[support] failed to queue offline', error);
          toast.error('Failed to save message. Please try again.');
          setIsSubmitting(false);
          return;
        });

      setIsSubmitted(true);
      toast.success(
        'Message saved offline — will send when you\'re back online. Please reopen the app when you have internet to ensure it\'s sent.',
        { duration: 8000 }
      );
      reset();
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  // Helper function to parse version string and compare versions
  const compareVersions = (a: FeatureItem, b: FeatureItem): number => {
    // Handle features without versions (shouldn't happen for 'new' status, but safe check)
    if (!a.version && !b.version) return 0;
    if (!a.version) return 1;
    if (!b.version) return -1;

    // Parse version strings (e.g., "v3.0.0" -> [3, 0, 0])
    const parseVersion = (version: string): number[] => {
      return version
        .replace(/^v/, '') // Remove 'v' prefix
        .split('.')
        .map(num => parseInt(num, 10));
    };

    const versionA = parseVersion(a.version);
    const versionB = parseVersion(b.version);

    // Compare major, minor, patch versions
    for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
      const partA = versionA[i] || 0;
      const partB = versionB[i] || 0;
      if (partA !== partB) {
        return partB - partA; // Descending order (newest first)
      }
    }

    return 0;
  };

  const renderFeatureCard = (feature: FeatureItem) => (
    <motion.div
      key={feature.id}
      variants={item}
      className="p-6 rounded-lg border-2 bg-green-50 border-green-200"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center">
          <div className="p-2 rounded-lg mr-3 bg-green-100">
            <feature.icon className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{feature.title}</h3>
            {feature.version && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                <Star className="h-3 w-3 mr-1" />
                {feature.version}
              </span>
            )}
          </div>
        </div>
        <span className="inline-block px-2 py-1 rounded-full text-xs font-medium text-center bg-green-200 text-green-800">
          New!
        </span>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
    </motion.div>
  );

  // Get the display name for the user
  const displayName = preferredDisplayName || currentUser?.displayName || 'User';
  const userEmail = currentUser?.email || '';

  // Add structured data for FAQ
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      // Clean up the script when component unmounts
      const scripts = document.head.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent?.includes('"@type":"FAQPage"')) {
          document.head.removeChild(script);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      className="max-w-6xl mx-auto space-y-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Header */}
      <motion.div
        className="text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-center mb-4">
          <HelpCircle className="h-12 w-12 text-secondary-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-800 mb-4">Help Center</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Everything you need to know about managing your cruise vacations with Cruising Along
        </p>
      </motion.div>

      {/* Getting Started Guide */}
      <motion.section
        variants={container}
        initial="hidden"
        animate="show"
        className="bg-white rounded-lg shadow-md p-8"
      >
        <motion.div variants={item} className="flex items-center mb-6">
          <Book className="h-6 w-6 text-secondary-500 mr-3" />
          <h2 className="text-2xl font-semibold text-gray-800">Getting Started</h2>
        </motion.div>

        {/* Feature grid: gap-8 provides appropriate spacing for large feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.div variants={item} className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
                <span className="text-secondary-600 font-semibold">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Create Your Account</h3>
                <p className="text-gray-600">Sign up with your email and create a secure password to get started.</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
                <span className="text-secondary-600 font-semibold">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Add Your First Cruise</h3>
                <p className="text-gray-600">Click the "Add Cruise" button and fill in your cruise details including dates, ship, and cabin information.</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
                <span className="text-secondary-600 font-semibold">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Explore Features</h3>
                <p className="text-gray-600">Add ports of call, travel details, and explore your cruise statistics in the Highlights section.</p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={item} className="bg-blue-50 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-4">Key Features</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Ship className="h-5 w-5 text-blue-600" />
                <span className="text-blue-800">Track multiple cruises</span>
              </div>
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-blue-600" />
                <span className="text-blue-800">Visual cruise calendar</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-blue-600" />
                <span className="text-blue-800">Port and excursion planning</span>
              </div>
              <div className="flex items-center space-x-3">
                <BarChart className="h-5 w-5 text-blue-600" />
                <span className="text-blue-800">Cruise statistics and highlights</span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* App Availability Section */}
      <motion.section
        variants={container}
        initial="hidden"
        animate="show"
        className="bg-gradient-to-r from-secondary-50 to-blue-50 rounded-lg shadow-md p-8 border border-secondary-200"
      >
        <motion.div variants={item} className="flex items-center mb-6">
          <Smartphone className="h-6 w-6 text-secondary-500 mr-3" />
          <h2 className="text-2xl font-semibold text-gray-800">Available Everywhere</h2>
        </motion.div>

        <motion.div variants={item} className="mb-6">
          <p className="text-gray-600 mb-6">
            Cruising Along is available across multiple platforms, so you can access your cruise information wherever you are.
            Whether you're on your computer, Android device, or iPhone, your data syncs seamlessly across all platforms.
          </p>
        </motion.div>

        <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Web Version */}
          <a
            href="https://keep.cruisingalong.com"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border-2 border-transparent hover:border-secondary-300 group"
          >
            <div className="flex items-center mb-4">
              <div className="p-3 bg-secondary-100 rounded-lg mr-4 group-hover:bg-secondary-200 transition-colors">
                <Globe className="h-6 w-6 text-secondary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Web App</h3>
                <p className="text-sm text-gray-500">Access anywhere</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Use Cruising Along directly in your web browser at keep.cruisingalong.com
            </p>
            <div className="flex items-center text-primary-600 hover:text-primary-500 font-medium text-sm transition-colors">
              <span>Visit Website</span>
              <ExternalLink className="h-4 w-4 ml-2" />
            </div>
          </a>

          {/* Android App */}
          <a
            href="https://play.google.com/store/apps/details?id=com.grozio.cruisetracker"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border-2 border-transparent hover:border-secondary-300 group"
          >
            <div className="flex items-center mb-4">
              <div className="p-3 bg-green-100 rounded-lg mr-4 group-hover:bg-green-200 transition-colors">
                <Smartphone className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Android App</h3>
                <p className="text-sm text-gray-500">Google Play Store</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Download the Android app from the Google Play Store for your Android device
            </p>
            <div className="flex items-center text-primary-600 hover:text-primary-500 font-medium text-sm transition-colors">
              <span>Get on Google Play</span>
              <ExternalLink className="h-4 w-4 ml-2" />
            </div>
          </a>

          {/* iOS App */}
          <a
            href="https://apps.apple.com/us/app/cruising-along/id6752914595"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border-2 border-transparent hover:border-secondary-300 group"
          >
            <div className="flex items-center mb-4">
              <div className="p-3 bg-blue-100 rounded-lg mr-4 group-hover:bg-blue-200 transition-colors">
                <Smartphone className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">iOS App</h3>
                <p className="text-sm text-gray-500">Apple App Store</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm mb-4">
              Download the iOS app from the Apple App Store for your iPhone or iPad
            </p>
            <div className="flex items-center text-primary-600 hover:text-primary-500 font-medium text-sm transition-colors">
              <span>Get on App Store</span>
              <ExternalLink className="h-4 w-4 ml-2" />
            </div>
          </a>
        </motion.div>
      </motion.section>

      {/* Features Section */}
      <motion.section
        variants={container}
        initial="hidden"
        animate="show"
        className="bg-white rounded-lg shadow-md p-8"
      >
        <motion.div variants={item} className="flex items-center mb-6">
          <Sparkles className="h-6 w-6 text-secondary-500 mr-3" />
          <h2 className="text-2xl font-semibold text-gray-800">What's New</h2>
        </motion.div>

        <motion.div variants={item} className="mb-8">
          <p className="text-gray-600 mb-6">
            Stay up to date with the latest features and improvements to Cruising Along.
            We're constantly working to make your cruise planning experience even better!
          </p>
        </motion.div>

        {/* New Features */}
        <motion.div variants={item}>
          <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <Star className="h-5 w-5 text-green-500 mr-2" />
            Recently Released
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {featuresData
              .filter(feature => feature.status === 'new')
              .sort(compareVersions)
              .map(feature => renderFeatureCard(feature))}
          </div>
        </motion.div>

        <motion.div variants={item} className="mt-8 p-4 bg-gradient-to-r from-secondary-50 to-blue-50 rounded-lg border border-secondary-200">
          <div className="flex items-start">
            <Sparkles className="h-5 w-5 text-secondary-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-secondary-900 mb-1">Have a feature request?</h4>
              <p className="text-secondary-700 text-sm">
                We'd love to hear your ideas! Use the contact form below to suggest new features or improvements.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        variants={container}
        initial="hidden"
        animate="show"
        className="bg-white rounded-lg shadow-md p-8"
      >
        <motion.div variants={item} className="flex items-center mb-6">
          <Search className="h-6 w-6 text-secondary-500 mr-3" />
          <h2 className="text-2xl font-semibold text-gray-800">Frequently Asked Questions</h2>
        </motion.div>

        {/* Category Filter */}
        <motion.div variants={item} className="mb-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === category.value
                    ? 'bg-secondary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* FAQ Items */}
        <motion.div variants={item} className="space-y-4">
          {filteredFAQs.map((faq) => (
            <div key={faq.id} className="border border-gray-200 rounded-lg">
              <button
                onClick={() => toggleFAQ(faq.id)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-800">{faq.question}</span>
                {expandedFAQ === faq.id ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </button>
              {expandedFAQ === faq.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-6 pb-4"
                >
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </motion.div>
              )}
            </div>
          ))}
        </motion.div>
      </motion.section>

      {/* Additional Resources - Now comes before Contact Support */}
      <motion.section
        variants={container}
        initial="hidden"
        animate="show"
        className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-lg shadow-md p-8 text-white"
      >
        <motion.div variants={item} className="text-center">
          <Settings className="h-12 w-12 text-blue-200 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-4">Need More Help?</h2>
          <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
            Our support team is here to help you make the most of Cruising Along.
            Don't hesitate to reach out if you have any questions or suggestions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-blue-800 rounded-lg p-4">
              <h3 className="font-semibold mb-2">Response Time</h3>
              <p className="text-blue-200">Usually within 24-48 hours</p>
            </div>
            <div className="bg-blue-800 rounded-lg p-4">
              <h3 className="font-semibold mb-2">Support Hours</h3>
              <p className="text-blue-200">Monday - Friday, 9 AM - 5 PM EST</p>
            </div>
          </div>
        </motion.div>
      </motion.section>

      {/* Feedback Form - Now comes after Additional Resources */}
      <motion.section
        variants={container}
        initial="hidden"
        animate="show"
        className="bg-white rounded-lg shadow-md p-8"
      >
        <motion.div variants={item} className="flex items-center mb-6">
          <Send className="h-6 w-6 text-secondary-500 mr-3" />
          <h2 className="text-2xl font-semibold text-gray-800">Contact Support</h2>
        </motion.div>

        {isSubmitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Thank You!</h3>
            <p className="text-gray-600">
              We've received your message and strive to respond to all messages within 24-48 hours.
            </p>
          </motion.div>
        ) : (
          <motion.div variants={item}>
            <p className="text-gray-600 mb-6">
              Can't find what you're looking for? Send us a message and we'll help you out!
            </p>

            {/* User Info Display */}
            {currentUser && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Contacting support as:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <User className="h-4 w-4 text-gray-500 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{displayName}</p>
                      <p className="text-xs text-gray-500">Name</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Send className="h-4 w-4 text-gray-500 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{userEmail}</p>
                      <p className="text-xs text-gray-500">Email</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form
              name="support"
              method="POST"
              onSubmit={handleSubmit(onSubmitFeedback)}
              className="space-y-6"
            >

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <select
                    id="type"
                    {...register('type', { required: 'Please select a type' })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 ${errors.type ? 'border-red-500' : 'border-gray-300'
                      }`}
                  >
                    <option value="">Select type</option>
                    <option value="support">General Support</option>
                    <option value="bug">Bug Report</option>
                    <option value="feature">Feature Request</option>
                    <option value="feedback">General Feedback</option>
                  </select>
                  {errors.type && (
                    <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    id="subject"
                    type="text"
                    {...register('subject', { required: 'Subject is required' })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 ${errors.subject ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Brief description of your inquiry"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  id="message"
                  rows={5}
                  {...register('message', { required: 'Message is required' })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 ${errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Please provide as much detail as possible..."
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting || !currentUser}
                  className="flex items-center px-6 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : (
                    <Send className="h-5 w-5 mr-2" />
                  )}
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>

              {!currentUser && (
                <p className="text-sm text-gray-500 text-center">
                  You must be logged in to send a support message.
                </p>
              )}
            </form>
          </motion.div>
        )}
      </motion.section>
    </motion.div>
  );
};

export default Help;